import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const LEAD_STATUSES = ["new", "in_progress", "closed"];
const CASE_STATUSES = ["open", "escalated", "resolved"];

function StatusSelect({ value, options, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs">
      {options.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
    </select>
  );
}

function Row({ title, meta, body, right, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 last:border-0" data-testid={testid}>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-900 break-words">{title}</p>
        <p className="text-xs text-slate-500 break-words">{meta}</p>
        {body && <p className="mt-1 text-sm text-slate-600 break-words">{body}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">{right}</div>
    </div>
  );
}

export default function OwnerRequests() {
  const [data, setData] = useState({ leads: [], cases: [], counts: {} });
  const load = () => api.get("/admin/requests").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (kind, id, status) => {
    try { await api.patch(`/admin/${kind}/${id}`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const c = data.counts || {};

  return (
    <div className="space-y-6" data-testid="owner-requests">
      <p className="text-sm text-slate-500">Read-only overview of incoming help requests. Welly and the watchdog handle routine support automatically — this view is for exceptions only.</p>
      <div className="flex flex-wrap gap-2" data-testid="owner-requests-counts">
        <Badge variant="secondary">{c.leads_new || 0} new requests</Badge>
        <Badge variant="secondary">{c.newsletter_optins || 0} newsletter opt-ins</Badge>
        <Badge variant="secondary">{c.cases_open || 0} open support cases</Badge>
      </div>

      <section>
        <h3 className="font-semibold text-slate-900 mb-2">Help Center requests</h3>
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="owner-leads-list">
          {data.leads.length === 0 && <p className="p-6 text-sm text-slate-500">No requests yet.</p>}
          {data.leads.map((l) => (
            <Row key={l.id} testid={`owner-lead-${l.id}`}
              title={`${l.name} · ${l.email}`}
              meta={`${l.interest}${l.company ? ` · ${l.company}` : ""} · ${l.created_at?.slice(0, 16).replace("T", " ")}`}
              body={l.message}
              right={<>
                {l.newsletter_consent
                  ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`owner-lead-newsletter-${l.id}`}>Newsletter: yes</Badge>
                  : <Badge variant="secondary" data-testid={`owner-lead-newsletter-${l.id}`}>Newsletter: no</Badge>}
                <StatusSelect value={l.status || "new"} options={LEAD_STATUSES} onChange={(s) => update("leads", l.id, s)} testid={`owner-lead-status-${l.id}`} />
              </>} />
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-semibold text-slate-900 mb-2">Support cases (from Welly)</h3>
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="owner-cases-list">
          {data.cases.length === 0 && <p className="p-6 text-sm text-slate-500">No support cases.</p>}
          {data.cases.map((k) => (
            <Row key={k.id} testid={`owner-case-${k.id}`}
              title={`${k.category || "support"} · ${k.severity || "P3"}`}
              meta={`${k.user_email || "—"} · ${k.role || "—"} · ${k.created_at?.slice(0, 16).replace("T", " ")}`}
              body={k.summary}
              right={<StatusSelect value={k.status || "open"} options={CASE_STATUSES} onChange={(s) => update("cases", k.id, s)} testid={`owner-case-status-${k.id}`} />} />
          ))}
        </div>
      </section>
    </div>
  );
}
